import { useMemo } from "react";
import { useConstants } from "../constant/constantProvider";
import { useRbac } from "./useRbac";
import { useAuth } from "./useAuth";
import useIsMobile from "./useIsMobile";
import { RBAC_APPS } from "../constant/rbacConfig";
import { ROUTES, ROLE_ACCESS } from "../constant/keyConstants";

// Icons
import genieIcon from "../assets/new-images/genie-chat-logo.svg";
import procurementIcon from "../assets/new-images/procurement-logo.svg";
import translateIcon from "../assets/new-images/translate-logo.svg";
import gmtIcon from "../assets/new-images/gmt-logo.svg";
import deepInsightIcon from "../assets/icons/app-icons/deep-insight-logo.svg"
import userManagementIcon from "../assets/new-images/setting-icon.svg";
import talentPulse360Icon from "../assets/icons/app-icons/talent-pulse-360-logo.svg";

import { DEBUG_MODE_ENABLED } from "../constant/globalConstants";

/**
 * Custom hook to get accessible apps for the current user
 * Filters based on RBAC data, mobile/desktop view, and user role
 * 
 * @returns {Object} Object containing lists of accessible apps
 */
export const useAccessibleApps = () => {
    const { rbacData, isRbacLoaded } = useRbac();
    const { role } = useAuth();
    const isMobile = useIsMobile();
    const {
        CHAT_GENIE, GENIE_DESCRIPTION,
        PROCUREMENT_AI, PROCUREMENT_DESCRIPTION,
        TRANSLATE_AI, TRANSLATE_DESCRIPTION,
        GMT, GMT_DESCRIPTION,
        DEEP_INSIGHTS, DEEP_INSIGHTS_DESCRIPTION,
        USER_MANAGEMENT, USER_MANAGEMENT_DESCRIPTION,
        TALENT_PULSE_360, TALENT_PULSE_DESCRIPTION
    } = useConstants();

    const appsMetadata = useMemo(() => [
        {
            id: "chat-genie",
            title: CHAT_GENIE,
            description: GENIE_DESCRIPTION,
            icon: genieIcon,
            path: ROUTES.CHAT,
            rbacKey: RBAC_APPS.CHAT_GENIE
        },
        {
            id: "procurement-ai",
            title: PROCUREMENT_AI,
            description: PROCUREMENT_DESCRIPTION,
            icon: procurementIcon,
            path: ROUTES.PROCUREMENT_AI,
            rbacKey: RBAC_APPS.PROCUREMENT
        },
        {
            id: "translate-ai",
            title: TRANSLATE_AI,
            description: TRANSLATE_DESCRIPTION,
            icon: translateIcon,
            path: ROUTES.TRANSLATE,
            rbacKey: RBAC_APPS.TRANSLATION,
            hideOnMobile: true
        },
        {
            id: "gmt",
            title: GMT,
            description: GMT_DESCRIPTION,
            icon: gmtIcon,
            path: ROUTES.GMT_CHAT,
            rbacKey: RBAC_APPS.GMT
        },
        {
            id: "deep-insights",
            title: DEEP_INSIGHTS,
            description: DEEP_INSIGHTS_DESCRIPTION,
            icon: deepInsightIcon,
            path: ROUTES.DEEP_INSIGHTS_CHAT,
            rbacKey: RBAC_APPS.DEEP_INSIGHTS
        },
        {
            id: "talent-pulse-360",
            title: TALENT_PULSE_360,
            description: TALENT_PULSE_DESCRIPTION,
            icon: talentPulse360Icon,
            path: ROUTES.TALENT_PULSE_360,
            rbacKey: RBAC_APPS.TALENT_PULSE_360
        },
        {
            id: "user-management",
            title: USER_MANAGEMENT || "User Management",
            description: USER_MANAGEMENT_DESCRIPTION,
            icon: userManagementIcon,
            path: ROUTES.USER_MANAGEMENT,
            roleReq: ROLE_ACCESS.SUPERADMIN,
            isManagement: true
        }
    ], [
        CHAT_GENIE, GENIE_DESCRIPTION, PROCUREMENT_AI, PROCUREMENT_DESCRIPTION,
        TRANSLATE_AI, TRANSLATE_DESCRIPTION, GMT, GMT_DESCRIPTION,
        DEEP_INSIGHTS, DEEP_INSIGHTS_DESCRIPTION, USER_MANAGEMENT, USER_MANAGEMENT_DESCRIPTION,
        TALENT_PULSE_360, TALENT_PULSE_DESCRIPTION
    ]);

    const accessibleApps = useMemo(() => {
        if (!isRbacLoaded) return [];

        // If debug mode is on, show all apps
        if (DEBUG_MODE_ENABLED) {
            return appsMetadata.filter(app => !app.hideApp);
        }

        return appsMetadata.filter(app => {
            // Check if app is explicitly hidden
            if (app.hideApp) return false;

            // First check if role is required (management apps)
            if (app.roleReq) {
                return role === app.roleReq;
            }

            // Hidden on Mobile rule
            if (isMobile && app.hideOnMobile) return false;

            // Hidden on Desktop rule
            if (!isMobile && app.hideOnDesktop) return false;

            // Show the app if it's public or if the user has a key for it in their rbacData
            return app.isPublic || (rbacData && !!rbacData[app.rbacKey]);
        });
    }, [appsMetadata, rbacData, isRbacLoaded, isMobile, role]);

    return {
        accessibleApps, // All accessible apps including User Management for SuperAdmins
        isLoaded: isRbacLoaded
    };
};
